import { inject, Injectable } from '@angular/core';
import { IAcardoVoucherTicketContext } from '../../../cart/core/interfaces/IAcardoVoucherTicketContext';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { CardsStore } from '../../../cards/core/stores/CardsStore';
import { PriceCategoryGildepassService } from '../../../cart/core/services/PriceCategoryGildepassService';
import { IShowPriceCategory } from '../interfaces/IShowPriceCategory';
import {
  ISellingOverviewPriceCategoryGateOutcome
} from '../interfaces/ISellingOverviewPriceCategoryGateOutcome';
import { ShowsStore } from '../stores/ShowsStore';
import { ITerminalShowCashPrice } from '../../../terminals/core/interfaces/ITerminalShowCashPrice';
import { CinevilleUtils } from '../../../cart/core/utils/CinevilleUtils';
import { ISellingOverviewPriceRowGateRequest } from '../interfaces/ISellingOverviewPriceRowGateRequest';

@Injectable({
  providedIn: 'root'
})
export class SellingOverviewPriceCategoryGateService {

  private readonly _showsStore = inject(ShowsStore);
  private readonly _cartStore = inject(CartStore);
  private readonly _cardsStore = inject(CardsStore);
  private readonly _priceCategoryGildepassService = inject(PriceCategoryGildepassService);

  public evaluate(request: ISellingOverviewPriceRowGateRequest): ISellingOverviewPriceCategoryGateOutcome {
    const category = this.resolveCategory(request.priceCategoryId);

    if (!category) {
      return { kind: 'blocked' };
    }

    const ticketsNeedingCardCount = this.countTicketsWithCategory(request.showId, category.id) + request.seatCount;

    if (this._priceCategoryGildepassService.isAcardoCategory(category)) {
      if (request.rowAmount === null) {
        return { kind: 'blocked' };
      }

      return {
        kind: 'acardo-code',
        category,
        requestedSeatCount: request.seatCount,
        syntheticTicket: this.buildAcardoTicketContext(request.showId, request.rowAmount)
      };
    }

    if (category.flagCineville) {
      const showBegin = this.resolveShowBegin(request.showId);

      if (!showBegin) {
        return { kind: 'blocked' };
      }

      if (CinevilleUtils.isCinevilleCheckInTooEarly(showBegin)) {
        return { kind: 'cineville-early' };
      }

      return {
        kind: 'proceed',
        seatCount: 1,
        requestedSeatCount: request.seatCount,
        postAdd: 'cineville',
        category
      };
    }

    // One reduction card per ticket: with fewer unused cards than seats, add as many seats as there are cards.
    if (this.categoryUsesReductionCard(category)) {
      const unusedCardCount = this.countCustomerCardsForCategory(category)
        - this.countTicketsWithCategory(request.showId, category.id);

      if (unusedCardCount <= 0) {
        return {
          kind: 'card-modal',
          category,
          seatCount: request.seatCount,
          priceAreaId: request.priceAreaId,
          showId: request.showId
        };
      }

      return {
        kind: 'proceed',
        seatCount: Math.min(request.seatCount, unusedCardCount),
        requestedSeatCount: request.seatCount,
        postAdd: 'none',
        category
      };
    }

    if (this.needsCardCodeModalForPriceCategory(category, ticketsNeedingCardCount)) {
      return {
        kind: 'card-modal',
        category,
        seatCount: request.seatCount,
        priceAreaId: request.priceAreaId,
        showId: request.showId
      };
    }

    if (!this.hasRequiredCardAttached(category)) {
      return {
        kind: 'card-required',
        cardTypeName: this.getRequiredCardName(category) ?? ''
      };
    }

    return {
      kind: 'proceed',
      seatCount: request.seatCount,
      requestedSeatCount: request.seatCount,
      postAdd: 'none',
      category
    };
  }

  public resolveRowAmount(
    prices: readonly ITerminalShowCashPrice[],
    priceCategoryId: number,
    priceAreaId: number
  ): number | null {
    const row = prices.find(
      p => p.priceCategoryId === priceCategoryId && p.priceAreaId === priceAreaId
    );

    return row?.amount ?? null;
  }

  public hasRequiredCardAttached(category: IShowPriceCategory): boolean {
    if (!category.cardTypeId) {
      return true;
    }

    return this._cartStore.customerCards().some(c => c.cardTypeId === category.cardTypeId);
  }

  public getRequiredCardName(category: IShowPriceCategory): string | null {
    if (!category.cardTypeId) {
      return null;
    }

    return this._cardsStore.allCardTypes()
      .find(ct => ct.cardTypeId === category.cardTypeId)?.name ?? null;
  }

  public categoryUsesReductionCard(category: IShowPriceCategory): boolean {
    if (!category.cardTypeId) {
      return false;
    }

    const cardType = this._cardsStore.allCardTypes().find(ct => ct.cardTypeId === category.cardTypeId);

    return !!cardType?.flagReductionCard;
  }

  public countCustomerCardsForCategory(category: IShowPriceCategory): number {
    if (!category.cardTypeId) {
      return 0;
    }

    return this._cartStore.customerCards().filter(c => +c.cardTypeId === +category.cardTypeId).length;
  }

  public countTicketsWithCategory(showId: number, priceCategoryId: number): number {
    return this._cartStore.cartItemsTickets()
      .filter(t => t.showId === showId && +t.priceCategoryId === +priceCategoryId)
      .length;
  }

  public needsCardCodeModalForPriceCategory(
    priceCat: IShowPriceCategory,
    ticketsNeedingCardCount: number
  ): boolean {
    if (!priceCat.cardTypeId) {
      return false;
    }

    if (this.categoryUsesReductionCard(priceCat)) {
      return this.countCustomerCardsForCategory(priceCat) < ticketsNeedingCardCount;
    }

    return !this.hasRequiredCardAttached(priceCat);
  }

  public isCardWrongTypeForCategory(cardNr: string, category: IShowPriceCategory): boolean {
    if (!category.cardTypeId) {
      return true;
    }

    const card = this._cartStore.customerCards().find(c => c.cardNr === cardNr);

    return !card || card.cardTypeId !== category.cardTypeId;
  }

  private resolveCategory(priceCategoryId: number): IShowPriceCategory | undefined {
    return this._showsStore.showPriceCategories().find(c => +c.id === +priceCategoryId);
  }

  private resolveShowBegin(showId: number): string | null {
    const selectedShow = this._showsStore.selectedShow();
    const show = this._showsStore.shows().find(s => s.id === showId)
      ?? (selectedShow?.id === showId ? selectedShow : undefined);

    return show?.beginning ?? null;
  }

  private buildAcardoTicketContext(showId: number, totalPrice: number): IAcardoVoucherTicketContext {
    return {
      showId,
      totalPrice,
      itemId: 0,
      position: 0
    };
  }
}
